/**
 * Session storage (§13, §56).
 *
 * The honest version of a compromise, because §56 asks for something the
 * backend does not currently offer:
 *
 *   §56 "Prefer secure HttpOnly cookie/session design where compatible."
 *
 * The backend issues bearer tokens in the response body, so an HttpOnly
 * cookie is not available without a backend change. Given that, the least-bad
 * split is:
 *
 *   access token  -> memory only. Never touches disk. An XSS payload has to
 *                    run *while the tab is open* to see it, and it expires in
 *                    15 minutes regardless.
 *   refresh token -> localStorage. It has to survive a reload, because a
 *                    child who reopens the PWA and finds themselves signed
 *                    out will not go and find the WhatsApp link again.
 *
 * The residual risk is real and worth naming: XSS can read localStorage, so a
 * successful script injection yields a refresh token. The mitigations are the
 * ones §42 lists — no `dangerouslySetInnerHTML` for AI output, sanitise
 * dynamic content, CSP — plus rotation on the backend, which invalidates a
 * stolen refresh token the moment the real client uses theirs.
 *
 * Moving to HttpOnly cookies would remove that risk entirely and is written
 * up in docs/adr/0001.
 */

import { z } from 'zod';

const STORAGE_KEY = 'preplift.session.v1';

/**
 * The number this device last signed in with. Not a credential.
 *
 * It exists because the first thing real children said about PrepLift was
 * that typing a phone number to get back in is miserable, and they were
 * right: a ten-year-old on a shared phone typed ten digits to reach their
 * homework. Holding the number means the sign-in screen can be one button.
 *
 * It is kept under its own key, outside the session blob, because the two
 * have different lifetimes: a session ending is ordinary and must not cost
 * the convenience, while an explicit sign-out means the device is being
 * given up and takes the number with it (see `clearEverything`).
 */
const IDENTIFIER_KEY = 'preplift.lastIdentifier.v1';

/*
 * There is no second key here any more.
 *
 * The hand-off used to leave a "way back" token on the device, under its own
 * key and deliberately not cleared by `clearSession`, so a child tapping
 * sign-out could not strand their parent. Profiles removed the need for it:
 * the device always holds a family session, and a child's session is enough
 * to find its parent. One fewer credential sitting in a child's browser
 * storage is the part worth noticing.
 */

const persistedSchema = z.object({
  refreshToken: z.string().min(1),
  userId: z.string(),
  role: z.enum(['student', 'parent', 'admin', 'service']),
});

export type PersistedSession = z.infer<typeof persistedSchema>;

export interface ActiveSession extends PersistedSession {
  accessToken: string;
  /** Epoch ms. Used to refresh slightly early rather than on a 401. */
  expiresAt: number;
}

/** In memory only — deliberately not exported. */
let accessToken: string | null = null;
let expiresAt = 0;

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string, expiresInSeconds: number): void {
  accessToken = token;
  expiresAt = Date.now() + expiresInSeconds * 1000;
}

export function clearAccessToken(): void {
  accessToken = null;
  expiresAt = 0;
}

/**
 * True when the token is gone or close enough to expiry that a request would
 * likely 401 mid-flight. Refreshing 60s early turns a visible failure into an
 * invisible one.
 */
export function accessTokenIsStale(): boolean {
  return accessToken === null || Date.now() > expiresAt - 60_000;
}

function storage(): Storage | null {
  try {
    // Private browsing on iOS throws on access rather than returning null.
    return window.localStorage;
  } catch {
    return null;
  }
}

export function loadPersisted(): PersistedSession | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = persistedSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function persist(session: PersistedSession): void {
  storage()?.setItem(STORAGE_KEY, JSON.stringify(session));
}

/**
 * §42/§56: "Clear sensitive local state on logout/account switch" and
 * "Never mix state between children."
 *
 * Clears every PrepLift key, not just the session one, so a draft or a
 * cached preference from one child cannot be read by the next.
 */
export function clearSession(): void {
  clearAccessToken();
  const store = storage();
  if (!store) return;
  const doomed: string[] = [];
  for (let i = 0; i < store.length; i += 1) {
    const key = store.key(i);
    // Everything PrepLift wrote goes, with one exception: the number this
    // device last used. There used to be a different exception -- the
    // parent's way back, which belonged to them rather than to the session
    // being cleared, and which profiles removed.
    //
    // The number is not a credential and does not belong to the session that
    // is ending; it is the reason the next sign-in is one tap instead of ten
    // digits. `clearEverything` is what takes it, because only an explicit
    // sign-out means the device is being given up.
    if (key === IDENTIFIER_KEY) continue;
    if (key?.startsWith('preplift.')) doomed.push(key);
  }
  doomed.forEach((key) => store.removeItem(key));
  try {
    window.sessionStorage.clear();
  } catch {
    /* private mode */
  }
}

/**
 * A full sign-out: the session *and* the parent's way back.
 *
 * Used when the parent themselves leaves, rather than when a session simply
 * ends. After this the device has no PrepLift credential of any kind.
 */
export function clearEverything(): void {
  clearSession();
  forgetIdentifier();
}

export function rememberIdentifier(identifier: string): void {
  const trimmed = identifier.trim();
  if (!trimmed) return;
  try {
    storage()?.setItem(IDENTIFIER_KEY, trimmed);
  } catch {
    /* a device that cannot remember it just asks again */
  }
}

export function recalledIdentifier(): string | null {
  try {
    const value = storage()?.getItem(IDENTIFIER_KEY);
    return value && value.trim() ? value : null;
  } catch {
    return null;
  }
}

export function forgetIdentifier(): void {
  try {
    storage()?.removeItem(IDENTIFIER_KEY);
  } catch {
    /* nothing to forget */
  }
}
